// Copyright ©️ 2026 eVolve MEP, LLC

import React, { Fragment, useState } from 'react';

import type { LabelTypeId, Label } from '@evolvemep/api-ts';
import { Button, Card, Divider, Flex, getDefaultZIndex, Loader, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { getDeleteLabelMessage } from 'components/Mantine/Labels/helpers';
import { CreateOrEditLabel } from 'components/Mantine/Labels/ManageLabelsModal/CreateOrEditLabel';
import { EditableLabel } from 'components/Mantine/Labels/ManageLabelsModal/EditableLabel';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useLabels } from 'hooks/typeCacheHooks/useLabels/useLabels';
import { useHasAccess } from 'hooks/user/useHasAccess';

type Props = {
  labelTypeId: LabelTypeId;
};

// This modal is launched from a popover, so we need to ensure it appears above the popover.
const MODAL_Z_INDEX = getDefaultZIndex('popover');

export const ManageLabelsModal = ({ labelTypeId }: Props) => {
  const [opened, setOpened] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [labelToEdit, setLabelToEdit] = useState<Label | null>(null);
  const [labelToDelete, setLabelToDelete] = useState<Label | null>(null);
  const { labels, loading, deleteLabel } = useLabels(labelTypeId);
  const deleteLabelMessage = getDeleteLabelMessage(labelTypeId);
  const [searchPhrase, setSearchPhrase] = useState('');

  const hasAccess = useHasAccess('labels', 'write');
  if (!hasAccess) return null;

  return (
    <>
      <Button
        fullWidth
        size="compact-xs"
        variant="subtle"
        onClick={() => {
          setOpened(true);
        }}
      >
        Manage labels
      </Button>
      <WrappedModal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Manage labels"
        hideModalButtons={true}
        zIndex={MODAL_Z_INDEX}
      >
        <Flex gap="sm">
          <TextInput
            flex={1}
            placeholder="Search..."
            value={searchPhrase}
            onChange={(e) => setSearchPhrase(e.target.value)}
          />
          <Button
            leftSection={<EvolveIcon icon="Add" size="sm" />}
            variant="light"
            disabled={creatingLabel || isNotNil(labelToEdit) || loading}
            onClick={() => {
              setCreatingLabel(true);
            }}
          >
            New label
          </Button>
        </Flex>
        <Card withBorder={true} padding={0}>
          {loading && (
            <Flex align="center" justify="center" w="100%" py="md">
              <Loader />
            </Flex>
          )}
          {!loading && (isNil(labels) || labels.length === 0) && !creatingLabel ? (
            <Flex py="sm" px="md" c="dimmed">
              No labels have been created yet.
            </Flex>
          ) : (
            <>
              {creatingLabel && (
                <>
                  <CreateOrEditLabel
                    label={null}
                    labelTypeId={labelTypeId}
                    onLabelUpdated={() => setCreatingLabel(false)}
                    onCancel={() => setCreatingLabel(false)}
                  />
                  {labels.length > 0 && <Divider w="100%" />}
                </>
              )}
              {labels
                .filter((l) => l.name.toLowerCase().includes(searchPhrase.trim().toLowerCase()))
                .map((label, index) => (
                  <Fragment key={label.labelId}>
                    <EditableLabel
                      label={label}
                      labelTypeId={labelTypeId}
                      editing={labelToEdit?.labelId === label.labelId}
                      editable={isNil(labelToEdit) && !creatingLabel}
                      onEditLabel={setLabelToEdit}
                      onCancel={() => setLabelToEdit(null)}
                      onLabelUpdated={() => setLabelToEdit(null)}
                      onDeleteLabel={setLabelToDelete}
                    />
                    {index < labels.length - 1 && <Divider w="100%" />}
                  </Fragment>
                ))}
            </>
          )}
        </Card>
      </WrappedModal>
      <VariableTrackingModal
        variable={labelToDelete}
        title="Delete label"
        zIndex={MODAL_Z_INDEX + 1}
        size="sm"
        buttonColor="red"
        confirmationText="Delete"
        onClose={() => setLabelToDelete(null)}
        onConfirm={async (label) => {
          await deleteLabel(label.labelId).then(() => {
            notifications.show({
              title: 'Label deleted',
              message: `Label "${label.name}" successfully deleted.`,
              color: 'green',
            });
          });
        }}
      >
        {(label) => (
          <>
            <Text>
              Delete label <b>{label.name}</b>?
            </Text>
            {deleteLabelMessage && <Text>{deleteLabelMessage}</Text>}
          </>
        )}
      </VariableTrackingModal>
    </>
  );
};
